import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import Spinner from '@/components/Spinner';
import { STORAGE_DAYS, TRIAL_DAYS } from '@/config';
import { useTrialDiscount } from '@/hooks/user';
import IconCheckCircleFilled from '@/icons/check-circle.svg';
import IconPercentDiscount from '@/icons/redeem.svg';
import planPreviewImg from '@/images/plan-preview.svg';
import routes from '@/routes';
import { setPrice } from '@/store/checkout';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';
import { cn } from '@/utils';
import { localizedRoute } from '@/utils/locale';
import { formatPrice } from '@/utils/money';

function PlanCardUnified({ plansData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loggedIn = useSelector(state => state.profile.loggedIn);
	const { t } = useTranslation();
	const trialDiscount = useTrialDiscount();

	const isSettingsBilling = Boolean(useMatch(routes.myAccount));
	const isUnsubscribePage = Boolean(
		useMatch(localizedRoute(routes.unsubscribe))
	);
	const isTrialDiscountVisible = isSettingsBilling && trialDiscount;
	const { loading, monthlyPlan, trialPlan } = plansData;

	const features = t('plans.unified.features', {
		returnObjects: true,
		storageDays: STORAGE_DAYS
	});

	const monthlyPrice = formatPrice({
		amount: monthlyPlan?.baseAmount,
		currency: monthlyPlan?.currency
	});
	const trialPrice = formatPrice({
		amount: isTrialDiscountVisible ? 0 : trialPlan?.baseAmount,
		currency: trialPlan?.currency
	});

	const handleClick = () => {
		dispatch(setPrice({ id: trialPlan?.handlerId }));

		if (loggedIn) {
			return navigate(routes.checkout);
		}

		dispatch(
			openAuthModal({
				type: AUTH_MODAL_TYPES.SIGN_UP,
				redirect: routes.checkout
			})
		);
	};

	if (loading) {
		return (
			<div className="h-100 relative flex w-full items-center justify-center">
				<Spinner className="absolute inset-0 m-auto text-black" size={40} />
			</div>
		);
	}

	return (
		<div
			className={cn(
				'w-full rounded-sm border border-grey-200 bg-white',
				'mx-auto flex px-4 py-6 md:pl-5 md:pr-9',
				isSettingsBilling
					? 'border-black lg:max-w-full 2xl:min-h-[380px]'
					: 'z-10 max-w-[814px]'
			)}
		>
			<div
				className={cn(
					'mr-9 hidden w-[328px] shrink-0 grow items-center justify-center rounded-lg bg-grey-100 md:flex'
				)}
			>
				<img
					src={planPreviewImg}
					width="194px"
					height="172px"
					alt=""
					aria-hidden
				/>
			</div>

			<div
				className={cn(
					'flex w-full flex-col',
					isSettingsBilling && '2xl:flex-row 2xl:items-center 2xl:gap-10'
				)}
			>
				<div className={cn(isSettingsBilling && '2xl:max-w-[400px]')}>
					<p className="mb-1 text-base font-semibold 2xl:text-xl">
						{t('plans.unified.title')}
					</p>
					<p className="mb-6 text-xs text-black md:mb-7 lg:text-sm">
						{t('plans.unified.subtitle')}
					</p>
					<div className="mb-1 flex items-end gap-2">
						<p className="text-[32px] leading-10">{monthlyPrice}</p>
						<p className="pb-1 text-base">
							{t('plans.unified.periodicity')}
						</p>
					</div>
					<p
						className={cn(
							'text-xs text-grey-500',
							!isUnsubscribePage && 'mb-6 md:mb-7',
							isTrialDiscountVisible && 'mb-2 md:mb-2'
						)}
					>
						<Trans
							i18nKey="plans.unified.description"
							components={{
								color: (
									<span
										className={cn(
											isTrialDiscountVisible && 'font-bold text-red'
										)}
									/>
								)
							}}
							values={{ trialDays: TRIAL_DAYS, trialPrice }}
						/>
					</p>

					{isTrialDiscountVisible && (
						<p
							className={cn(
								'flex w-fit items-center justify-center gap-2 md:justify-start',
								'rounded-lg bg-light-red text-sm font-semibold text-red',
								'mb-6 px-2 py-1 md:mb-7'
							)}
						>
							<IconPercentDiscount className="size-5" />
							{t('fastCheckout.trialDiscount')}
						</p>
					)}
					<Button
						className={cn('w-full', isUnsubscribePage && 'hidden')}
						onClick={handleClick}
					>
						{t('plans.unified.button', { trialPrice })}
					</Button>
				</div>

				<hr
					className={cn(
						'my-5 h-px w-full bg-grey-200 md:mt-7',
						isSettingsBilling &&
							'2xl:my-0 2xl:h-auto 2xl:w-px 2xl:self-stretch'
					)}
				/>

				<div className="flex flex-col gap-3">
					<p className="text-sm font-semibold">
						{t('plans.unified.featuresTitle')}
					</p>
					<ul className="flex flex-col gap-2">
						{features.map((feature, ix) => (
							<li
								className="flex items-center gap-1 text-xs text-grey-500"
								key={ix}
							>
								<IconCheckCircleFilled className="size-5 shrink-0 text-black" />
								{feature}
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}

export default PlanCardUnified;
